import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchOverview, type Overview } from '@/api/superadmin';
import { Skeleton } from '@/components/primitives/skeleton';
import { SuperAdminPage } from '@/components/superadmin/superadmin-layout';

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="border-stroke-soft bg-bg-white flex flex-col gap-1 rounded-xl border p-5">
      <span className="text-paragraph-xs text-text-soft uppercase tracking-wide">{label}</span>
      <span className="text-title-h4 text-text-strong tabular-nums">{value}</span>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-stroke-soft bg-bg-white flex flex-col gap-3 rounded-xl border p-5">
      <h2 className="text-label-sm text-text-strong">{title}</h2>
      {children}
    </section>
  );
}

/** A section the API could not load renders as an honest gap, not a crash. */
function Unavailable() {
  return <p className="text-paragraph-sm text-text-soft">This section could not be loaded.</p>;
}

export function SuperAdminOverviewPage() {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOverview()
      .then(setOverview)
      .catch((caught) => setError((caught as Error).message));
  }, []);

  if (error) {
    return (
      <SuperAdminPage title="Overview">
        <p className="text-paragraph-sm text-error-base">{error}</p>
      </SuperAdminPage>
    );
  }

  if (!overview) {
    return (
      <SuperAdminPage title="Overview">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </SuperAdminPage>
    );
  }

  const totalMessages = (overview.messagesByDay ?? []).reduce((sum, row) => sum + row.count, 0);
  const byChannel = new Map<string, number>();

  for (const row of overview.messagesByDay ?? []) {
    byChannel.set(row.channel, (byChannel.get(row.channel) ?? 0) + row.count);
  }

  return (
    <SuperAdminPage title="Overview" description="Every tenant on this deployment, at a glance.">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {overview.totals ? (
          <>
            <Stat label="Tenants" value={overview.totals.tenants} />
            <Stat label="Active (30 days)" value={overview.totals.activeTenants} />
            <Stat label="Subscribers" value={overview.totals.subscribers} />
          </>
        ) : (
          <div className="sm:col-span-3">
            <Unavailable />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Messages sent (last 30 days)">
          {overview.messagesByDay ? (
            <>
              <span className="text-title-h5 text-text-strong tabular-nums">{totalMessages}</span>
              {byChannel.size === 0 ? (
                <p className="text-paragraph-sm text-text-soft">Nothing sent in this window.</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {[...byChannel.entries()].map(([channel, count]) => (
                    <li key={channel} className="flex items-center gap-3">
                      <span className="text-paragraph-sm text-text-sub w-20 capitalize">{channel}</span>
                      <div className="bg-bg-weak h-2 flex-1 overflow-hidden rounded-full">
                        <div
                          className="bg-primary-base h-full rounded-full"
                          style={{ width: `${totalMessages ? (count / totalMessages) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="text-paragraph-sm text-text-sub w-12 text-right tabular-nums">{count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <Unavailable />
          )}
        </Panel>

        <Panel title="Delivery outcomes (last 30 days)">
          {overview.deliveryOutcomes ? (
            <div className="grid grid-cols-3 gap-3">
              {/*
                Only confirmed outcomes are shown. "Accepted" is deliberately absent:
                it means a provider took custody, not that anything was delivered.
              */}
              <div className="flex flex-col">
                <span className="text-title-h5 text-success-base tabular-nums">
                  {overview.deliveryOutcomes.delivered}
                </span>
                <span className="text-paragraph-xs text-text-soft">Delivered</span>
              </div>
              <div className="flex flex-col">
                <span className="text-title-h5 text-error-base tabular-nums">{overview.deliveryOutcomes.bounced}</span>
                <span className="text-paragraph-xs text-text-soft">Bounced</span>
              </div>
              <div className="flex flex-col">
                <span className="text-title-h5 text-warning-base tabular-nums">
                  {overview.deliveryOutcomes.suppressed}
                </span>
                <span className="text-paragraph-xs text-text-soft">Suppressed</span>
              </div>
            </div>
          ) : (
            <Unavailable />
          )}
        </Panel>

        <Panel title="Newest tenants">
          {overview.newestTenants ? (
            overview.newestTenants.length === 0 ? (
              <p className="text-paragraph-sm text-text-soft">No tenants yet.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {overview.newestTenants.map((tenant) => (
                  <li key={tenant._id} className="flex items-center justify-between">
                    <Link
                      to={`/superadmin/tenants/${tenant._id}`}
                      className="text-paragraph-sm text-text-strong hover:underline"
                    >
                      {tenant.name}
                    </Link>
                    <span className="text-paragraph-xs text-text-soft">
                      {new Date(tenant.createdAt).toLocaleDateString()}
                    </span>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <Unavailable />
          )}
        </Panel>

        <Panel title="Recent activity">
          {overview.recentAuditEvents ? (
            overview.recentAuditEvents.length === 0 ? (
              <p className="text-paragraph-sm text-text-soft">Nothing recorded yet.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {overview.recentAuditEvents.map((event, index) => (
                  <li key={`${event.action}-${event.createdAt}-${index}`} className="flex items-center justify-between">
                    <span className="text-paragraph-sm text-text-sub">{event.action.replace(/_/g, ' ')}</span>
                    <span className="text-paragraph-xs text-text-soft">
                      {new Date(event.createdAt).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <Unavailable />
          )}
        </Panel>
      </div>
    </SuperAdminPage>
  );
}

export default SuperAdminOverviewPage;
